import { Dispatch, FunctionComponent, SetStateAction, memo, useCallback, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import {
  IgdbGameSearchResult,
  StoryCardTeaserImageStyle,
  StoryCardType,
  User,
  storyCardApi,
  useLazyFourPigDbGameByIdQuery,
  useLazyFourPigDbSearchGamesQuery,
} from '../../../../services';
import { GalleryItem } from '../../../models';
import { AutoComplete } from '../../AutoComplete';
import { LinkPostArticleFields, LinkPostFields } from '../CreateCardModal';
import { LinkPostArticle } from '../LinkPost/LinkPostArticle';
import { buildIgdbSource, getReleaseYear } from './helpers';

import classNames from 'classnames';
import classes from './IgdbPost.module.scss';

interface IgdbPostProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  linkPostFields: LinkPostFields;
  setLinkPostFields: Dispatch<SetStateAction<LinkPostFields>>;
  setLinkPostArticleGalleryItems: (galleryItems: GalleryItem[]) => void;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  setActiveLinkPostSettingsTabIndex: (index: number) => void;
  postInStoryTeaserStyle: StoryCardTeaserImageStyle;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
}

export const IgdbPost: FunctionComponent<IgdbPostProps> = memo(
  ({
    loading,
    setLoading,
    linkPostFields,
    setLinkPostFields,
    setLinkPostArticleGalleryItems,
    setSettingsOpen,
    canOpenSettings,
    setActiveLinkPostSettingsTabIndex,
    postInStoryTeaserStyle,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
  }) => {
    const { t } = useTranslation();

    const [searchGames] = useLazyFourPigDbSearchGamesQuery();

    const [fetchGame] = useLazyFourPigDbGameByIdQuery();

    const [parseUrl] = storyCardApi.endpoints.storyCardParseUrl.useMutation();

    const gamesFetchQuery = useCallback(
      (query: string, page?: number) => searchGames({ query, page }),
      [searchGames]
    );

    const { url, article } = linkPostFields;

    const getCoverImage = useCallback(
      async (coverUrl?: string): Promise<GalleryItem | null> => {
        if (!coverUrl) {
          return null;
        }

        try {
          const { gallery } = await parseUrl({ url: coverUrl }).unwrap();
          return (gallery[0] as GalleryItem | undefined) ?? null;
        } catch (_) {
          return null;
        }
      },
      [parseUrl]
    );

    const onGameSelect = useCallback(
      async ([selectedGame]: IgdbGameSearchResult[]) => {
        if (!selectedGame) {
          return;
        }

        setLoading(true);

        try {
          const game = await fetchGame({ id: selectedGame.id }).unwrap();

          if (!game) {
            return;
          }

          const { name, summary, storyline, coverUrl, websiteUrl, genres, platforms } = game;

          const coverImage = await getCoverImage(coverUrl);

          setLinkPostFields({
            url: websiteUrl ?? '',
            type: StoryCardType.ARTICLE,
            article: {
              type: StoryCardType.ARTICLE,
              title: name ?? '',
              abstract: summary ?? storyline ?? '',
              contentAuthor: '',
              sourceName: buildIgdbSource({ genres, platforms }),
              headline: '',
              gallery: coverImage ? [coverImage] : [],
              styles: { teaserImageStyle: postInStoryTeaserStyle },
            },
          });
        } catch (_) {
          toast.error(t('common.error-message'));
        } finally {
          setLoading(false);
        }
      },
      [fetchGame, getCoverImage, postInStoryTeaserStyle, setLinkPostFields, setLoading, t]
    );

    const setArticle = useCallback(
      (article: LinkPostArticleFields) => setLinkPostFields({ ...linkPostFields, article }),
      [linkPostFields, setLinkPostFields]
    );

    const gameItemTemplate = useCallback(
      ({ name, coverUrl, firstReleaseDate }: IgdbGameSearchResult) => {
        const releaseYear = getReleaseYear(firstReleaseDate);

        return (
          <div className={classes['igdb-post__item']}>
            <div className={classes['igdb-post__item-fields']}>
              <div className={classes['igdb-post__item-name']}>{name}</div>
              {releaseYear && <div className={classes['igdb-post__item-date']}>{releaseYear}</div>}
            </div>
            {coverUrl && (
              <div
                className={classes['igdb-post__item-cover']}
                style={{ backgroundImage: `url(${coverUrl})` }}
              />
            )}
          </div>
        );
      },
      []
    );

    const content = useMemo(() => {
      if (article) {
        return (
          <div
            className={classNames(classes['igdb-post__selected'], {
              [classes['igdb-post__selected--mobile']]: isMobileOnly,
            })}
          >
            <LinkPostArticle
              loading={loading}
              setLoading={setLoading}
              url={url}
              articleData={article}
              setArticle={setArticle}
              setLinkPostArticleGalleryItems={setLinkPostArticleGalleryItems}
              setActiveLinkPostSettingsTabIndex={setActiveLinkPostSettingsTabIndex}
              setSettingsOpen={setSettingsOpen}
              canOpenSettings={canOpenSettings}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          </div>
        );
      }

      if (loading) {
        return (
          <div className={classes['igdb-post__loader']}>
            <Skeleton height={'6rem'} />
          </div>
        );
      }

      return (
        <div className={classes['igdb-post__scrollable']}>
          <div className={classes['igdb-post__autocomplete-wrapper']}>
            <AutoComplete
              propToIdentify={'id'}
              propToDisplay={'name'}
              value={[]}
              fetchQuery={gamesFetchQuery}
              itemTemplate={gameItemTemplate}
              placeholder={t('addContent.igdb-post-search')}
              onChange={onGameSelect}
              singleSelect
              closeOnSelect
            />
            <div className={classes['igdb-post__autocomplete-hint']}>
              {t('addContent.igdb-post-search-hint')}
            </div>
          </div>
        </div>
      );
    }, [
      article,
      canOpenSettings,
      gameItemTemplate,
      gamesFetchQuery,
      loading,
      mentionedUsers,
      onGameSelect,
      postInStoryAllowMentions,
      setActiveLinkPostSettingsTabIndex,
      setArticle,
      setLinkPostArticleGalleryItems,
      setLoading,
      setSettingsOpen,
      setUserMentionModalOpen,
      t,
      url,
    ]);

    return (
      <div
        className={classNames(classes['igdb-post'], {
          [classes['igdb-post--mobile']]: isMobileOnly,
        })}
      >
        {content}
      </div>
    );
  }
);
